import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ShoppingBag, LogOut, LogIn, UserPlus, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

/**
 * Slide-in mobile menu with categories + auth links.
 * `open` and `onClose` control visibility. Adds branded top identity.
 */
export default function MobileMenuDrawer({ open, onClose }) {
  const { user, logout } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const [cats, setCats] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/products").then(({ data }) => setCats(data.categories || [])).catch(() => {});
  }, [open]);

  const close = () => onClose?.();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60]"
            onClick={close}
            data-testid="mobile-drawer-scrim"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 h-full w-[86%] max-w-sm bg-white z-[70] flex flex-col shadow-2xl"
            data-testid="mobile-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-slate-100">
              <Link to="/" onClick={close} className="flex items-center gap-2" data-testid="mobile-drawer-logo">
                <img src="/products/img_10.png" alt="Morning Soft" className="h-9 w-9 object-contain" />
                <span className="font-display text-lg font-extrabold text-[#173a7a]">
                  Morning<span className="text-amber-500">Soft</span>
                </span>
              </Link>
              <button onClick={close} data-testid="mobile-drawer-close" className="p-2 rounded-full hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>

            {/* User section */}
            <div className="px-5 py-5 border-b border-slate-100 bg-sky-50/40">
              {user ? (
                <div data-testid="mobile-drawer-user">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Signed in as</p>
                  <p className="font-display font-bold text-slate-900 mt-1">{user.name || user.email}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={close} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#173a7a]" data-testid="mobile-drawer-admin-link">
                      <LayoutDashboard size={14} /> Admin dashboard
                    </Link>
                  )}
                  <button onClick={() => { logout(); close(); }} data-testid="mobile-drawer-logout"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-red-500">
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" onClick={close} data-testid="mobile-drawer-login"
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#173a7a] text-white font-semibold text-sm">
                    <LogIn size={14} /> Sign in
                  </Link>
                  <Link to="/signup" onClick={close} data-testid="mobile-drawer-signup"
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full bg-white border border-slate-200 text-slate-900 font-semibold text-sm">
                    <UserPlus size={14} /> Sign up
                  </Link>
                </div>
              )}
            </div>

            {/* Nav */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-5 py-4">
                <p className="text-[11px] uppercase tracking-widest text-amber-500 font-bold mb-2">Browse</p>
                <div className="flex flex-col">
                  <Link to="/shop" onClick={close} data-testid="mobile-drawer-shop"
                    className="py-3 text-sm font-semibold text-slate-800 border-b border-slate-50">Shop — All products</Link>
                  {cats.map((c) => (
                    <Link key={c.slug} to={`/shop?category=${c.slug}`} onClick={close}
                      data-testid={`mobile-drawer-category-${c.slug}`}
                      className="py-3 text-sm font-medium text-slate-600 border-b border-slate-50 pl-3">
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="px-5 py-4">
                <p className="text-[11px] uppercase tracking-widest text-amber-500 font-bold mb-2">Company</p>
                <div className="flex flex-col">
                  <Link to="/about" onClick={close} data-testid="mobile-drawer-about"
                    className="py-3 text-sm font-semibold text-slate-800 border-b border-slate-50">About</Link>
                  <Link to="/contact" onClick={close} data-testid="mobile-drawer-contact"
                    className="py-3 text-sm font-semibold text-slate-800 border-b border-slate-50">Contact</Link>
                </div>
              </div>
            </div>

            {/* Footer cart button */}
            <div className="border-t border-slate-100 px-5 py-4">
              <button
                data-testid="mobile-drawer-cart"
                onClick={() => { setDrawerOpen(true); close(); }}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-full bg-[#173a7a] text-white font-semibold text-sm"
              >
                <span className="inline-flex items-center gap-2"><ShoppingBag size={16} /> Cart</span>
                {count > 0 && (
                  <span className="min-w-[24px] h-6 px-2 rounded-full bg-amber-400 text-[#173a7a] text-xs font-extrabold flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
